import React, { FC, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { OptionsObject, SnackbarContentCallback, useSnackbar } from 'notistack'

import api from '../../api'
import Notification from './Notification'
import { AsyncFunction } from '../../types/common'
import { useDebounce } from '../../hooks/useDebounce'
import { BaseNotification } from '../../types/notifications'
import { getBaseNotifications } from './notificationsHelpers'
import { useAllUnreadMessagesCount } from '../../hooks/contexts/chat/useAllUnreadMessagesCount'

const snackbarOptions = (
	el: BaseNotification,
	content: SnackbarContentCallback
): OptionsObject => ({
	autoHideDuration: 90000,
	anchorOrigin: {
		vertical: 'bottom',
		horizontal: 'right',
	},
	persist: false,
	preventDuplicate: true,
	content,
})

const UNREAD_COUNT_DEBOUNCE_TIME = 2500

const Notifications: FC = () => {
	const alert = useAlert()
	const numberOfUnreadMessages = useAllUnreadMessagesCount()
	const { enqueueSnackbar, closeSnackbar } = useSnackbar()
	const debouncedUnreadCount = useDebounce(
		numberOfUnreadMessages,
		UNREAD_COUNT_DEBOUNCE_TIME
	)

	useEffect(() => {
		if (!debouncedUnreadCount) return

		const latestChatNotification: BaseNotification = {
			type: 'latestChats',
			dueDate: new Date().getTime(),
		}

		enqueueSnackbar(
			latestChatNotification.dueDate,
			snackbarOptions(latestChatNotification, key => (
				<div key={key}>
					<Notification
						notification={latestChatNotification}
						onClose={() => closeSnackbar(key)}
					/>
				</div>
			))
		)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedUnreadCount])

	useEffect(() => {
		const getNotifications: AsyncFunction = async () => {
			try {
				const res = await api.notifications.getNotifications()

				const notificationsData = res.data?.result

				if (!notificationsData) return

				const notifications: BaseNotification[] =
					getBaseNotifications(notificationsData)

				notifications.forEach(el =>
					enqueueSnackbar(
						el.dueDate,
						snackbarOptions(el, key => (
							<div key={key}>
								<Notification
									notification={el}
									onClose={() => closeSnackbar(key)}
								/>
							</div>
						))
					)
				)
			} catch (e: any) {
				'message' in e && alert.error(e.message)
			}
		}
		getNotifications()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return <></>
}

export default Notifications
